import type {ReactElement} from 'react';
import {useAppDispatch} from '@/hooks';
import type {PanelContentProps, PanelHeaderProps} from '@Components/panel';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import {AiGenerationBox} from '@Components/ai-generation-box';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {AiGenerationPanelProps} from './ai-generation-panel.types';
import styles from './ai-generation-panel.module.scss';

function AiGenerationPanel<T>(props: AiGenerationPanelProps<T>): ReactElement {
  const dispatch = useAppDispatch();

  const closeAiGenerationModal = (): void => {
    dispatch(closeModal(MODALS_IDS.AI_GENERATION_MODAL));
  };

  const handleBack = (): void => {
    if (props.onBack) {
      props.onBack();
      return;
    }

    closeAiGenerationModal();
  };

  const handleClose = (): void => {
    if (props.onClose) {
      props.onClose();
      return;
    }

    closeAiGenerationModal();
  };

  const handleItemSelect = (item: T, prompt: string): void => {
    props.onItemSelect(item, prompt);

    if (props.closeOnItemSelect) {
      handleClose();
    }
  };

  const showBackButton = props.showBackButton ?? true;

  const renderHeader = (): ReactElement<PanelHeaderProps> => {
    return (
      <PanelHeader
        backButtonIconSize={IconSize.SIZE_ICON_16}
        className={`_borderless ${props.isOpenedInPopOver ? 'spacing-p-l-4 spacing-p-r-4' : ''}`}
        title={props.title}
        titleClasses="content-ai body-md-bold"
        showBackButton={showBackButton}
        showCloseButton={false}
        onBackButton={handleBack}
        onClose={handleClose}
        forceCenterAlignTitleWithActionItems
      />
    );
  };

  const renderContent = (): ReactElement<PanelContentProps> => {
    return (
      <PanelContent className={styles.content}>
        <AiGenerationBox<T>
          id={props.id}
          sessionStorageKey={props.sessionStorageKey}
          initialConfig={props.initialConfig}
          fetchRecents={props.fetchRecents}
          onGenerate={props.onGenerate}
          onItemSelect={handleItemSelect}
          getItemKey={props.getItemKey}
          options={props.options}
          renderItem={props.renderItem}
          aiPromptBoxPreviewText={props.aiPromptBoxPreviewText}
          showRetryOnError={props.showRetryOnError}
          customPopupClasses={styles.popupContainer}
          isMobile={props.isMobile}
        />
      </PanelContent>
    );
  };

  return <Panel className={styles.panel} panelHeader={renderHeader()} panelContent={renderContent()} />;
}

export default AiGenerationPanel;
